import { useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import type { VueDAigle } from '../../types.js';
import commun from './commun.module.css';
import { LienConsole, Montant, NonComptees, Section } from './composants.js';
import { useDroitsVue } from './droits.js';
import { useFormats } from './formats.js';
import { Courbe, HistoriqueCourt } from './graphiques.js';
import styles from './Revenus.module.css';

/** Les abonnements montrés d'abord ; le reste se déplie. */
const ABONNEMENTS_VISIBLES = 6;

const TONE_ETAT_ABONNEMENT: Record<string, string> = {
  actif: commun.pastilleVert!,
  essai: commun.pastilleAmbre!,
  suspendu: commun.pastilleGris!,
  resilie: commun.pastilleGris!,
};

function somme(valeurs: number[]): number {
  return valeurs.reduce((total, valeur) => total + valeur, 0);
}

/**
 * Le montant d'une ligne, ou `null` (« à saisir ») quand TOUTES ses lignes
 * sont à saisir : leur somme vaut 0, mais ce 0 n'est pas un chiffre. Une
 * ligne sans aucune entrée (aucun exploitant dans la tranche) garde son vrai
 * zéro.
 */
function montantOuASaisir(centimes: number, lignes: number, aSaisir: number): number | null {
  return lignes > 0 && aSaisir >= lignes ? null : centimes;
}

export function Revenus({ vue, numero }: { vue: VueDAigle; numero: string }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { peut } = useDroitsVue(vue);
  const { revenus, indicateurs } = vue;

  const abonnements = somme(revenus.abonnementsParPalier.map((l) => l.montantCentimes));
  const abonnementsNombre = somme(revenus.abonnementsParPalier.map((l) => l.nombre));
  const abonnementsASaisir = somme(revenus.abonnementsParPalier.map((l) => l.aSaisir));
  const horsGrille = revenus.forfaitsHorsGrille;
  const forfaits =
    somme(revenus.forfaitsParTranche.map((l) => l.montantCentimes)) + horsGrille.montantCentimes;
  const forfaitsNombre =
    somme(revenus.forfaitsParTranche.map((l) => l.exploitants)) + horsGrille.exploitants;
  const forfaitsASaisir =
    somme(revenus.forfaitsParTranche.map((l) => l.aSaisir)) + horsGrille.aSaisir;
  const ponctuels = somme(revenus.ponctuelsParCategorie.map((l) => l.montantCentimes));
  const ponctuelsASaisir = somme(revenus.ponctuelsParCategorie.map((l) => l.aSaisir));
  const total = revenus.totalMoisCentimes;
  const part = (centimes: number): number => (total > 0 ? centimes / total : 0);

  // Une catégorie ponctuelle ne dit pas combien de ses lignes sont saisies :
  // une somme nulle avec des lignes à saisir est tenue pour « à saisir ».
  const ponctuelOuASaisir = (centimes: number, aSaisir: number): number | null =>
    centimes === 0 && aSaisir > 0 ? null : centimes;
  const recurrentNombre = abonnementsNombre + forfaitsNombre;
  const recurrentASaisir = abonnementsASaisir + forfaitsASaisir;
  const affiches = {
    abonnements: montantOuASaisir(abonnements, abonnementsNombre, abonnementsASaisir),
    forfaits: montantOuASaisir(forfaits, forfaitsNombre, forfaitsASaisir),
    recurrent: montantOuASaisir(indicateurs.mrrCentimes, recurrentNombre, recurrentASaisir),
    ponctuels: ponctuelOuASaisir(ponctuels, ponctuelsASaisir),
  };
  // Le total du mois n'est « à saisir » que si RIEN n'est saisi, nulle part.
  const recurrentAbsent = recurrentNombre === 0 || affiches.recurrent === null;
  const ponctuelsAbsents =
    revenus.ponctuelsParCategorie.length === 0 || affiches.ponctuels === null;
  const totalAffiche =
    (affiches.recurrent === null || affiches.ponctuels === null) &&
    recurrentAbsent &&
    ponctuelsAbsents
      ? null
      : total;
  const partDe = (centimes: number | null): string =>
    centimes === null ? '' : f.pourcent(part(centimes));
  // Toutes les lignes que les sous-totaux ignorent, récurrent et ponctuel.
  const nonComptees = recurrentASaisir + ponctuelsASaisir;

  const historique = revenus.historiqueMrr;
  const peutGerer = peut('console:manage-subscriptions');

  return (
    <Section
      id="revenus"
      numero={numero}
      titre={t('vueDAigle.revenus.titre')}
      actions={
        peutGerer ? (
          <LienConsole vers="/finances?onglet=abonnements" bouton>
            {t('vueDAigle.revenus.gererAbonnements')}
          </LienConsole>
        ) : undefined
      }
    >
      <div className={commun.deuxColonnes}>
        <div>
          <p className={commun.sousTitre}>{t('vueDAigle.revenus.courbe')}</p>
          {historique.length >= 2 ? (
            <Courbe
              points={historique.map((p) => ({
                court: f.moisCourt(p.mois),
                long: f.moisLong(p.mois),
                valeur: p.mrrCentimes / 100,
              }))}
              legendeTableau={t('vueDAigle.revenus.courbeTableau', { count: historique.length })}
              enteteLibelle={t('vueDAigle.revenus.colMois')}
              enteteValeur={t('vueDAigle.revenus.colMrr')}
              formatAxe={f.axeEuros}
              formatValeur={(euros) => f.euros(Math.round(euros * 100))}
            />
          ) : (
            <HistoriqueCourt />
          )}
        </div>

        <div>
          <p className={commun.sousTitre}>
            {t('vueDAigle.revenus.repartition', { mois: f.moisLong(revenus.mois) })}
          </p>
          <div className={styles.barre} aria-hidden="true">
            <span className={styles.c1} style={{ width: `${part(abonnements) * 100}%` }} />
            <span className={styles.c2} style={{ width: `${part(forfaits) * 100}%` }} />
            <span className={styles.c3} style={{ width: `${part(ponctuels) * 100}%` }} />
          </div>
          <ul className={styles.repartition}>
            <li>
              <i className={`${styles.cle} ${styles.c1}`} aria-hidden="true" />
              <span>{t('vueDAigle.revenus.abonnements')}</span>
              <span className={commun.num}>
                <Montant centimes={affiches.abonnements} />
              </span>
              <span className={`${commun.num} ${styles.part}`}>{partDe(affiches.abonnements)}</span>
            </li>

            <li>
              <i className={`${styles.cle} ${styles.c2}`} aria-hidden="true" />
              <span>{t('vueDAigle.revenus.forfaits')}</span>
              <span className={commun.num}>
                <Montant centimes={affiches.forfaits} />
              </span>
              <span className={`${commun.num} ${styles.part}`}>{partDe(affiches.forfaits)}</span>
            </li>
            {revenus.forfaitsParTranche.map((ligne) => (
              <li key={ligne.libelle} className={styles.sous}>
                <span />
                <span>
                  {ligne.libelle}
                  <small>
                    {ligne.exploitants === 0
                      ? t('vueDAigle.revenus.aucunExploitant')
                      : t('vueDAigle.revenus.exploitants', { count: ligne.exploitants })}
                  </small>
                </span>
                <span className={commun.num}>
                  <Montant
                    centimes={montantOuASaisir(
                      ligne.montantCentimes,
                      ligne.exploitants,
                      ligne.aSaisir,
                    )}
                  />
                </span>
                <span className={commun.num} />
              </li>
            ))}
            {horsGrille.exploitants > 0 ? (
              <li className={styles.sous}>
                <span />
                <span>
                  {t('vueDAigle.revenus.horsGrille')}
                  <small>
                    {t('vueDAigle.revenus.exploitants', { count: horsGrille.exploitants })}
                  </small>
                </span>
                <span className={commun.num}>
                  <Montant
                    centimes={montantOuASaisir(
                      horsGrille.montantCentimes,
                      horsGrille.exploitants,
                      horsGrille.aSaisir,
                    )}
                  />
                </span>
                <span className={commun.num} />
              </li>
            ) : null}
            <li className={styles.total}>
              <span />
              <span>{t('vueDAigle.revenus.recurrent')}</span>
              <span className={commun.num}>
                <Montant centimes={affiches.recurrent} />
              </span>
              <span className={`${commun.num} ${styles.part}`}>{partDe(affiches.recurrent)}</span>
            </li>
            <li>
              <i className={`${styles.cle} ${styles.c3}`} aria-hidden="true" />
              <span>{t('vueDAigle.revenus.ponctuels')}</span>
              <span className={commun.num}>
                <Montant centimes={affiches.ponctuels} />
              </span>
              <span className={`${commun.num} ${styles.part}`}>{partDe(affiches.ponctuels)}</span>
            </li>
            {revenus.ponctuelsParCategorie.map((ligne) => (
              <li key={ligne.categorie} className={styles.sous}>
                <span />
                <span>{ligne.categorie}</span>
                <span className={commun.num}>
                  <Montant centimes={ponctuelOuASaisir(ligne.montantCentimes, ligne.aSaisir)} />
                </span>
                <span className={commun.num} />
              </li>
            ))}
            <li className={styles.total}>
              <span />
              <span>{t('vueDAigle.revenus.totalMois')}</span>
              <span className={commun.num}>
                <Montant centimes={totalAffiche} />
              </span>
              <span className={commun.num} />
            </li>
          </ul>
          <NonComptees nombre={nonComptees} />
          <p className={commun.noteDiscrete}>{t('vueDAigle.revenus.noteForfait')}</p>
        </div>
      </div>

      {vue.abonnements ? <Abonnements lignes={vue.abonnements} peutModifier={peutGerer} /> : null}
    </Section>
  );
}

/** La liste des abonnements : vue admin seulement (le champ est absent côté associé). */
function Abonnements({
  lignes,
  peutModifier,
}: {
  lignes: NonNullable<VueDAigle['abonnements']>;
  peutModifier: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const [toutVoir, setToutVoir] = useState(false);

  // Échéances les plus proches d'abord ; sans échéance, à la fin.
  const triees = [...lignes].sort((a, b) => {
    if (a.echeanceEngagementLe === b.echeanceEngagementLe) return 0;
    if (a.echeanceEngagementLe === null) return 1;
    if (b.echeanceEngagementLe === null) return -1;
    return a.echeanceEngagementLe.localeCompare(b.echeanceEngagementLe);
  });
  const visibles = toutVoir ? triees : triees.slice(0, ABONNEMENTS_VISIBLES);

  return (
    <div className={styles.abonnements}>
      <p className={commun.sousTitre}>
        {t('vueDAigle.revenus.abonnementsTitre')} ·{' '}
        {t('vueDAigle.revenus.abonnementsResume', { count: lignes.length })}
      </p>
      {lignes.length === 0 ? (
        <p className={commun.noteDiscrete}>{t('vueDAigle.revenus.aucunAbonnement')}</p>
      ) : (
        <>
          <div className={commun.tableauConteneur}>
            <table className={`${commun.table} ${commun.tableEmpile}`}>
              <thead>
                <tr>
                  <th scope="col">{t('vueDAigle.revenus.colGalerie')}</th>
                  <th scope="col">{t('vueDAigle.revenus.colPalier')}</th>
                  <th scope="col" className={commun.num}>
                    {t('vueDAigle.revenus.colMontant')}
                  </th>
                  <th scope="col" className={commun.num}>
                    {t('vueDAigle.revenus.colRemise')}
                  </th>
                  <th scope="col">{t('vueDAigle.revenus.colEtat')}</th>
                  <th scope="col" className={commun.num}>
                    {t('vueDAigle.revenus.colEcheance')}
                  </th>
                  <th scope="col">
                    <span className={commun.srOnly}>{t('vueDAigle.revenus.colAction')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((ligne) => (
                  <tr key={ligne.id}>
                    <td
                      className={`${commun.premiere} ${commun.cellulePrincipale}`}
                      data-label={t('vueDAigle.revenus.colGalerie')}
                    >
                      {ligne.galerie}
                    </td>
                    <td data-label={t('vueDAigle.revenus.colPalier')}>
                      {ligne.palier
                        ? t(`galeries.palier.${ligne.palier}`, { defaultValue: ligne.palier })
                        : '—'}
                    </td>
                    <td className={commun.num} data-label={t('vueDAigle.revenus.colMontant')}>
                      <Montant centimes={ligne.montantMensuelCentimes} />
                    </td>
                    <td className={commun.num} data-label={t('vueDAigle.revenus.colRemise')}>
                      {ligne.remisePointsDeBase > 0
                        ? f.pourcentDePoints(ligne.remisePointsDeBase)
                        : '—'}
                    </td>
                    <td data-label={t('vueDAigle.revenus.colEtat')}>
                      <span
                        className={`${commun.pastille} ${TONE_ETAT_ABONNEMENT[ligne.etat] ?? ''}`}
                      >
                        {t(`vueDAigle.revenus.etat.${ligne.etat}`, { defaultValue: ligne.etat })}
                      </span>
                    </td>
                    <td className={commun.num} data-label={t('vueDAigle.revenus.colEcheance')}>
                      {ligne.echeanceEngagementLe ? f.date(ligne.echeanceEngagementLe) : '—'}
                    </td>
                    <td className={commun.actionCellule}>
                      {peutModifier ? (
                        <LienConsole vers="/finances?onglet=abonnements">
                          {t('vueDAigle.revenus.modifier')}
                        </LienConsole>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={commun.tablePied}>
            <span>{t('vueDAigle.revenus.pied')}</span>
            {lignes.length > ABONNEMENTS_VISIBLES ? (
              <Button variant="ghost" size="sm" onClick={() => setToutVoir((v) => !v)}>
                {toutVoir
                  ? t('vueDAigle.revenus.reduire')
                  : t('vueDAigle.revenus.afficherTout', { count: lignes.length })}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
